import {
  IMealCreditFormFields,
  MealCreditPayload,
  mapToPayload,
} from 'src/components/oneCorp/features/MealCredit/utils/EnterpriseManageMealCreditsPage.utils';
import { useAddMealCredit } from 'src/components/oneCorp/features/MealCredit/components/Form/hooks/useAddMealCredit';
import { useUpdateMealCredit } from 'src/components/oneCorp/features/MealCredit/components/Form/hooks/useUpdateMealCredit';
import { IMealCredit } from 'src/components/oneCorp/features/MealCredit/utils/EnterpriseMealCreditsDetailsPage.utils';
import { SdkErrorResponse } from 'src/utilities/notifications';
import { IErrorResponse } from 'src/utilities/shared-types';
import { useMealCreditFormData } from 'src/components/oneCorp/features/MealCredit/components/Form/MealCreditDataProvider/MealCreditDataProvider';

export interface UseMealCreditForm {
  onFormSubmit: (props: OnFormSubmitProps) => Promise<void>;
}

export interface UseMealCreditFormSubmitProps {
  onAddMealCreditSuccess?: ({ id }: { id: string }) => Promise<void>;
  onAddMealCreditError?: (
    error: IErrorResponse | SdkErrorResponse
  ) => Promise<void>;
  onUpdateMealCreditSuccess?: () => Promise<void>;
  onUpdateMealCreditError?: (
    error: IErrorResponse | SdkErrorResponse
  ) => Promise<void>;
}

export interface OnFormSubmitProps {
  values: IMealCreditFormFields;
}

export const useMealCreditFormSubmit = ({
  onAddMealCreditError,
  onUpdateMealCreditSuccess,
  onAddMealCreditSuccess,
  onUpdateMealCreditError,
}: UseMealCreditFormSubmitProps): UseMealCreditForm => {
  const {
    formValues: {
      startDate,
      isMultiDaysLengthBudget,
      isRepeating,
      hasLocation,
      isMonthAvailable,
      monthAvailableTo,
      expirationDate,
    },
    corpDataValues: { corpId, corpData },
    mealCredit,
  } = useMealCreditFormData();

  const { addMealCredit } = useAddMealCredit({
    corpId,
    onSuccess: onAddMealCreditSuccess,
    onError: onAddMealCreditError,
  });

  const { updateMealCredit } = useUpdateMealCredit({
    corpId,
    onSuccess: onUpdateMealCreditSuccess,
    onError: onUpdateMealCreditError,
  });

  const getBaselinePayload = (
    mealCredit: IMealCredit
  ): Partial<MealCreditPayload> => {
    return {
      ...(mealCredit?.merchant_restrictions && {
        merchant_restrictions: mealCredit.merchant_restrictions,
      }),
      ...(mealCredit?.item_restrictions && {
        item_restrictions: mealCredit.item_restrictions,
      }),
    };
  };

  async function onFormSubmit({ values }: OnFormSubmitProps): Promise<void> {
    // send existing budgets through or nothing if adding a new credit
    const mealCreditBudgets = mealCredit ? mealCredit.tag_invites : null;

    // send existing prohibited_items through or nothing if adding a new credit
    const mealCreditProhibitedItems = mealCredit
      ? mealCredit.prohibited_items
      : null;

    const baselinePayload = getBaselinePayload(mealCredit);

    const newMealCredit = mapToPayload({
      baselinePayload,
      values,
      corpid: corpId,
      startDate,
      isMultiDaysLengthBudget,
      hasLocation,
      isRepeating,
      orderPolicyId: corpData?.orderPolicyId,
      budgets: mealCreditBudgets,
      mealCreditProhibitedItems,
      isMonthAvailable,
      monthAvailableTo,
      expirationDate,
    });
    if (mealCredit) {
      await updateMealCredit({
        ...newMealCredit,
        id: mealCredit.id,
      });
    } else {
      await addMealCredit(newMealCredit);
    }
  }

  return { onFormSubmit };
};
